import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Paper, Box, Button, Typography, IconButton, TextField, Divider, Table, TableHead, TableBody, TableCell, TableRow } from '@mui/material';
import { ApolloClient, ApolloProvider, gql, InMemoryCache, useQuery } from "@apollo/client";
import { Modal } from "./modal";
import { MoreVert } from '@mui/icons-material'
import { parse } from 'graphql';

const client = new ApolloClient({
  uri: localStorage.getItem('HEXHIVE_API') || 'http://localhost:7030/graphql',
  credentials: 'include',
  cache: new InMemoryCache()
})

export default function Root(props) {
  return (
    <ApolloProvider client={client}>
      <RootWrapped {...props} />
    </ApolloProvider>
  )
}

interface TypeField {
  name: string;
  type: {
    name: string,
    kind: string;
    ofType?: {
      name: string;
      fields?: TypeField[];
    }
  }
}

interface InputField {
  name: string;
  type: {
    name: string,
    kind: string;
    ofType?: {
      name: string;
      inputFields?: InputField[];
    }
  }
}

export interface RootProps{
  model: {
    name: string;
    input: string;
  };

  getData: () => Promise<any[]>;
  createData: (item: any) => Promise<void>;
  updateData: (item: any) => Promise<void>;
  deleteData: (item: any) => Promise<void>;

  onSelectRow?: (row: any) => void;
}

const RootWrapped : React.FC<RootProps> = (props) => {
  
  const { model, getData:_getData, createData, updateData, deleteData } = props || {};

  const parsedInput = useMemo(() => {
    try{
      return parse(props.model?.input || '').definitions.find((a) => a.kind == "InputObjectTypeDefinition") as any
    }catch(err){

    }
  }, [props.model?.input])


  const parsedType = useMemo(() => {
    try{
      return parse(props.model?.name || '').definitions.find((a) => a.kind == "ObjectTypeDefinition") as any
    }catch(err){

    }
  }, [props.model?.name])

  let gqldata: any;
  if(parsedInput == null || parsedType == null){
    const { data } = useQuery(gql`
      query ($inputType: String!, $type: String!) {
        
        ${parsedInput == null ? `inputType:__type(name:$inputType){
          name
          kind
          enumValues{
            name
          }
          inputFields {
            name
            type {
              name
              kind
              ofType {
                name
                kind
                enumValues{
                  name
                }
                inputFields {
                  name
                  type {
                    name
                    kind
                    enumValues{
                      name
                    }
                  }
                }
              }
            }
          }
        }`: ''}
        ${parsedType == null ? `type:__type(name: $type){
          name
          kind
          fields {
            name
            type {
              name
              kind
              ofType {
                name
                fields {
                  name
                  type {
                    name
                  }
                }
              }
            }
          }` : ''}
        }
      }  
    `, {
      fetchPolicy: 'no-cache',
      variables: {
        inputType: model?.input,
        type: model?.name
      }
    })
    gqldata = data;
  }

  

  const inputFields: InputField[] = gqldata?.inputType?.inputFields || parsedInput?.fields?.map((field: any) => ({name: field.name.value, type: {name: field.type.name.value, kind: field.type.kind == 'NamedType' ? 'SCALAR' : field.type.kind}})) || [];
  const fields: TypeField[] = gqldata?.type?.fields || parsedType?.fields?.map((field: any) => ({name: field.name.value, type: {name: field.type.name.value, kind: field.type.kind == 'NamedType' ? 'SCALAR' : field.type.kind}})) || [];

  const fetchFields = fields.filter((field) => field.type.kind == "OBJECT")

  const [ fetchData, setFetchData ] = useState<any>({});

  const [ introspectData, setIntrospectData ] = useState<any>({});

  const introspect = () => {
    if(fetchFields.length <= 0) return;

    client.query({query: gql`
      query {
        ${fetchFields?.map((field) => {
          return `
            ${field.type?.name}:__type(name: "${field.type?.name}"){
              name
              kind
              fields {
                name
                type {
                  name
                  kind
                }
              }
            }
          `
        }).join('\n')}
      }
    `}).then(({data}) => {
      //Introspection result

      setIntrospectData(data)
    })
  };


  const refreshFetchData = () => {
    if(Object.keys(introspectData).length == 0) return;
    if(fetchFields.length <= 0) return;

    //Query
    return client.query({
      query: gql`
        query {
          ${fetchFields?.map((field) => {
            return `
              ${field.type?.name}s {
                ${introspectData?.[field.type?.name]?.fields?.map((typeField) => {
                  return typeField?.type?.kind !== 'OBJECT' ? typeField?.name : ''
                }).join('\n')}
              }
            `
          }).join('\n')}
        }
      `,
      fetchPolicy: 'no-cache'
    }).then(({data}) => {
      setFetchData(data)
    }).catch((err) => {
      console.log("Err getting fetchData")
    })
  }

  useEffect(() => {
    refreshFetchData();
  }, [JSON.stringify(introspectData), JSON.stringify(fetchFields)])


  useEffect(() => {
    introspect()
  }, [fetchFields])


  const [selected, setSelected] = useState<any>(null);
  const [modalOpen, openModal] = useState(false);


  const [data, setData] = useState<any[]>([]);

  const getData = () => {
   return Promise.all([_getData?.(), refreshFetchData()]).then(([data, _]) => {
      setData(data);
    })
  }


  useEffect(() => {
    getData?.();
  }, [_getData])

  const keys = useMemo(() => {
    return fields.map((field) => field.name)
      .filter((a) => a.indexOf('__') < 0)
      .filter((a) => data?.map((x) => !Array.isArray(x?.[a])).indexOf(false) < 0)
      .filter((a) => data?.map((x) => x?.[a] != null).indexOf(true) > -1)
      // .filter((a) => typeof(data?.[0]?.[a]) != 'object')
  }, [data, fields])


  const closeModal = () => {

    openModal(false)
    setSelected(null);
  }

  const modelName = parsedType?.name?.value || model?.name;

  return <div style={{
    minHeight: 0,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: '8px'
  }}>
    <Modal
      model={{ name: modelName }}
      fetchData={fetchData}
      inputFields={inputFields}
      selected={selected}
      open={modalOpen}
      onDelete={() => {
        deleteData?.(selected).then(() => {
          getData?.().then(() => {
            closeModal()
          })
        })
      }}
      onSubmit={(item) => {
        if (item.id) {
          updateData?.(item).then(() => {
            getData?.().then(() => {
              closeModal()
            })
          })
        } else {
          createData?.(item).then(() => {
            getData?.().then(() => {
              closeModal()
            })
          })
        }

      }}
      onClose={closeModal}
    />
    <Box sx={{ display: 'flex', paddingBottom: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography>{modelName}</Typography>
      <Button onClick={() => openModal(true)} variant="contained">Add</Button>
    </Box>
    <Divider />
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'scroll' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {keys.map((key, ix) => (
              <TableCell key={`header-${ix}`}>
                {key}
              </TableCell>
            ))}
            <TableCell sx={{ width: '45px' }} padding="none"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((item, ix) => (
            <TableRow
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  background: "#dfdfdf"
                }
              }}
              key={`row-${ix}`}
              onClick={() => {
                props.onSelectRow?.(item)
              }}>
              {keys.map((key) => (
                <TableCell key={`row-${ix}-${key}`}>
                  {item[key]?.name || item[key]}
                </TableCell>
              ))}
              <TableCell sx={{ width: '45px' }} padding="none">
                <IconButton onClick={(e) => {
                  e.stopPropagation();
                  setSelected(item)
                  openModal(true)
                }}>
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  </div>;
}
